/**
 * Generic search-and-select modal for any InvenioRDM-compatible remote
 * repository. All repository-specific details (URLs, labels, field mapping)
 * are provided via props so the component can be reused for different sources.
 *
 * Props
 * -----
 * apiPrefix       string    Backend proxy prefix, e.g. "/api/b2inst"
 * buttonLabel     string    Trigger button text
 * modalTitle      string    Modal header text
 * toItemObject    func      (hit) => draftFieldObject | throws
 * isDuplicateItem func      (existingItems, hit) => bool
 * existingItems   array     Current field value (for duplicate detection)
 * onAddItem       func      (item) => void — called when user confirms selection
 */
import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Checkbox,
  Dimmer,
  Dropdown,
  Grid,
  Header,
  Icon,
  Input,
  Label,
  Loader,
  Message,
  Modal,
  Pagination,
  Segment,
} from "semantic-ui-react";
import { i18next } from "@translations/invenio_app_rdm/i18next";
import { fetchCommunities, fetchInstruments } from "./api";
import { reconcileFilters } from "./normalizers";

// Pure helper — outside component so useCallback deps stay clean.
function applyFacetReconciliation(newFacets, currentFilters) {
  return {
    facets: newFacets,
    filters: reconcileFilters(currentFilters, newFacets),
  };
}

// ---------------------------------------------------------------------------
// CommunitySelector
// ---------------------------------------------------------------------------
const CommunitySelector = ({ communities, selected, onChange, loading }) => {
  const options = [
    { key: "__all__", value: "", text: i18next.t("All communities") },
    ...communities.map((c) => ({
      key: c.id,
      value: c.id,
      text: c.title || c.slug,
    })),
  ];

  return (
    <div>
      <div style={{ fontWeight: "bold", marginBottom: 4 }}>
        {i18next.t("Community")}
      </div>
      <Dropdown
        fluid
        selection
        loading={loading}
        options={options}
        value={selected || ""}
        onChange={(_, { value }) => onChange(value || null)}
        aria-label={i18next.t("Filter by community")}
      />
    </div>
  );
};

CommunitySelector.propTypes = {
  communities: PropTypes.array.isRequired,
  selected: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

CommunitySelector.defaultProps = {
  selected: null,
  loading: false,
};

// ---------------------------------------------------------------------------
// FacetPanel — renders one terms facet column
// ---------------------------------------------------------------------------
const FacetPanel = ({ facets, filters, onChange }) => {
  if (!facets || facets.length === 0) return null;

  return (
    <div>
      {facets.map((facet) => {
        if (!facet.buckets || facet.buckets.length === 0) return null;
        const selected = filters[facet.name] || [];

        return (
          <div key={facet.name} style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: "bold", marginBottom: 4 }}>
              {facet.label}
            </div>
            {facet.buckets.map((bucket) => {
              const isChecked = selected.includes(bucket.key);
              return (
                <div key={bucket.key} style={{ marginBottom: 2 }}>
                  <Checkbox
                    label={`${bucket.label || bucket.key} (${bucket.count})`}
                    checked={isChecked}
                    onChange={(_, { checked }) => {
                      const next = checked
                        ? [...selected, bucket.key]
                        : selected.filter((v) => v !== bucket.key);
                      onChange(facet.name, next);
                    }}
                  />
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

FacetPanel.propTypes = {
  facets: PropTypes.array.isRequired,
  filters: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// SearchResultItem
// ---------------------------------------------------------------------------
const SearchResultItem = ({
  hit,
  onAdd,
  duplicate,
  addLabel,
  openLabel,
}) => {
  const { display, instrument, links } = hit;

  const typeLabel = (display.instrument_types || []).join(", ");
  const mfrLabel = (display.manufacturers || []).join(", ");
  const ownerLabel = (display.owners || []).join(", ");
  const varLabel = (display.measured_variables || []).join(", ");
  const subtitleParts = [typeLabel, mfrLabel, display.model].filter(Boolean);

  return (
    <Segment>
      <Grid>
        <Grid.Row>
          <Grid.Column width={13}>
            <Header as="h4" className="rel-mb-1">
              {instrument?.instrument_name || i18next.t("(no name)")}
            </Header>
            {subtitleParts.length > 0 && (
              <div className="mb-5">
                {subtitleParts.join(" · ")}
              </div>
            )}
            {ownerLabel && (
              <div className="rel-mb-1">
                <strong>{i18next.t("Owner:")} </strong>
                {ownerLabel}
              </div>
            )}
            {varLabel && (
              <div className="rel-mb-1">
                <strong>{i18next.t("Variables:")} </strong>
                {varLabel}
              </div>
            )}
            {instrument?.identifier && (
              <div className="mt-5">
                <Label size="tiny" color="grey">
                  {instrument.identifier}
                </Label>
              </div>
            )}
          </Grid.Column>
          <Grid.Column width={3} textAlign="right">
            {links?.html && (
              <Button
                as="a"
                href={links.html}
                target="_blank"
                rel="noopener noreferrer"
                size="tiny"
                basic
                className="mb-5 mr-0"
                icon
                labelPosition="left"
              >
                <Icon name="external alternate" />
                {openLabel}
              </Button>
            )}
            <Button
              size="tiny"
              primary
              disabled={duplicate || !instrument}
              onClick={() => onAdd(hit)}
              title={
                duplicate
                  ? i18next.t(
                    "This record is already included."
                  )
                  : undefined
              }
            >
              {addLabel}
            </Button>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Segment>
  );
};

SearchResultItem.propTypes = {
  hit: PropTypes.object.isRequired,
  onAdd: PropTypes.func.isRequired,
  duplicate: PropTypes.bool,
  addLabel: PropTypes.string.isRequired,
  openLabel: PropTypes.string.isRequired,
};

SearchResultItem.defaultProps = {
  duplicate: false,
};

// ---------------------------------------------------------------------------
// ExternalRecordSearchModal
// ---------------------------------------------------------------------------
const INITIAL_CRITERIA = { community: null, q: "", filters: {} };

const ExternalRecordSearchModal = ({
  apiPrefix,
  buttonLabel,
  modalTitle,
  openRecordLabel,
  addRecordLabel,
  duplicateMessage,
  toItemObject,
  isDuplicateItem,
  existingItems,
  onAddItem,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const [communities, setCommunities] = useState([]);
  const [facets, setFacets] = useState([]);
  const [draftCriteria, setDraftCriteria] = useState(INITIAL_CRITERIA);
  const [submittedCriteria, setSubmittedCriteria] = useState(null);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [loadingCommunities, setLoadingCommunities] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [error, setError] = useState(null);
  const [addError, setAddError] = useState(null);

  // ------------------------------------------------------------------
  // Core search response handler
  // ------------------------------------------------------------------

  const applySearchResponse = useCallback((data, criteria, pageNum) => {
    const { facets: newFacets, filters: reconciledFilters } =
      applyFacetReconciliation(data.facets || [], criteria.filters);

    setResults(data.hits || []);
    setTotal(data.total || 0);
    setPage(pageNum);
    setPageSize(data.size || 10);
    setFacets(newFacets);

    const reconciledCriteria = {
      ...criteria,
      filters: reconciledFilters,
    };

    setDraftCriteria(reconciledCriteria);
    setSubmittedCriteria(reconciledCriteria);
  }, []);

  const doSearch = useCallback(
    async (criteria, pageNum) => {
      setLoadingResults(true);
      setError(null);
      try {
        const resp = await fetchInstruments(apiPrefix, {
          community: criteria.community,
          q: criteria.q,
          page: pageNum,
          sort: "newest",
          filters: criteria.filters,
        });
        applySearchResponse(resp.data, criteria, pageNum);
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          i18next.t("The remote repository is temporarily unavailable.");
        setError(msg);
      } finally {
        setLoadingResults(false);
      }
    },
    [apiPrefix, applySearchResponse]
  );

  // ------------------------------------------------------------------
  // Open / close
  // ------------------------------------------------------------------

  const handleOpen = () => {
    setIsOpen(true);
    setDraftCriteria(INITIAL_CRITERIA);
    setSubmittedCriteria(null);
    setResults([]);
    setFacets([]);
    setTotal(0);
    setPage(1);
    setError(null);
    setAddError(null);
  };

  const handleClose = () => setIsOpen(false);

  // On open: fetch communities + initial search concurrently.
  useEffect(() => {
    if (!isOpen) return;

    setLoadingCommunities(true);
    fetchCommunities(apiPrefix)
      .then((resp) => setCommunities(resp.data.communities || []))
      .catch(() => setCommunities([]))
      .finally(() => setLoadingCommunities(false));

    doSearch(INITIAL_CRITERIA, 1);
    // doSearch is stable; INITIAL_CRITERIA is a module constant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ------------------------------------------------------------------
  // Community change → scoped search, reset filters
  // ------------------------------------------------------------------

  const handleCommunityChange = (communityId) => {
    const newCriteria = {
      community: communityId,
      q: draftCriteria.q,
      filters: {},
    };
    setDraftCriteria(newCriteria);
    doSearch(newCriteria, 1);
  };

  // ------------------------------------------------------------------
  // Text / facet changes — do NOT search automatically
  // ------------------------------------------------------------------

  const handleQueryChange = (_, data) => {
    const value = data?.value ?? "";

    setDraftCriteria((prev) => ({
      ...prev,
      q: value,
    }));
  };

  const handleFacetChange = (facetName, values) => {
    setDraftCriteria((prev) => ({
      ...prev,
      filters: { ...prev.filters, [facetName]: values },
    }));
  };

  // ------------------------------------------------------------------
  // Explicit search (button or Enter)
  // ------------------------------------------------------------------

  const handleSearch = () => doSearch(draftCriteria, 1);

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  // ------------------------------------------------------------------
  // Pagination
  // ------------------------------------------------------------------

  const handlePageChange = (_, { activePage }) => {
    const criteria = submittedCriteria || draftCriteria;
    doSearch(criteria, activePage);
  };

  // ------------------------------------------------------------------
  // Add item
  // ------------------------------------------------------------------

  const handleAdd = (hit) => {
    setAddError(null);
    if (isDuplicateItem(existingItems, hit)) {
      setAddError(duplicateMessage);
      return;
    }
    try {
      const item = toItemObject(hit);
      onAddItem(item);
      handleClose();
    } catch (err) {
      setAddError(err.message);
    }
  };

  const totalPages = pageSize > 0 ? Math.ceil(total / pageSize) : 0;

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  return (
    <>
      <Button
        type="button"
        icon
        labelPosition="left"
        onClick={handleOpen}
        className="mt-1"
      >
        <Icon name="search" />
        {buttonLabel}
      </Button>

      <Modal open={isOpen} onClose={handleClose} size="large" closeIcon>
        <Modal.Header>{modalTitle}</Modal.Header>

        <Modal.Content scrolling>
          {/* Community selector */}
          <div className="mb-15">
            <CommunitySelector
              communities={communities}
              selected={draftCriteria.community}
              onChange={handleCommunityChange}
              loading={loadingCommunities}
            />
          </div>

          {/* Search bar */}
          <div className="mb-15">
            <Input
              fluid
              placeholder={i18next.t("Search…")}
              value={draftCriteria.q}
              onChange={handleQueryChange}
              onKeyDown={handleSearchKeyDown}
              action={
                <Button
                  primary
                  type="button"
                  onClick={handleSearch}
                  loading={loadingResults}
                  disabled={loadingResults}
                  labelPosition="left"
                  icon
                >
                  <Icon name="search" />
                  {i18next.t("Search")}
                </Button>
              }
              aria-label={i18next.t("Search")}
            />
          </div>

          {/* Errors */}
          {error && (
            <Message negative>
              <Message.Header>{i18next.t("Search error")}</Message.Header>
              <p>{error}</p>
            </Message>
          )}
          {addError && (
            <Message warning>
              <p>{addError}</p>
            </Message>
          )}

          <Grid stackable>
            {/* Facet panel */}
            {facets.length > 0 && (
              <Grid.Column width={5}>
                <FacetPanel
                  facets={facets}
                  filters={draftCriteria.filters}
                  onChange={handleFacetChange}
                />
              </Grid.Column>
            )}

            {/* Results */}
            <Grid.Column width={facets.length > 0 ? 11 : 16}>
              <Dimmer.Dimmable dimmed={loadingResults}>
                <Dimmer active={loadingResults} inverted>
                  <Loader />
                </Dimmer>

                {!loadingResults && !error && results.length === 0 && (
                  <Message info>
                    <p>{i18next.t("No matching records were found.")}</p>
                  </Message>
                )}

                {results.map((hit) => (
                  <SearchResultItem
                    key={hit.id}
                    hit={hit}
                    onAdd={handleAdd}
                    duplicate={isDuplicateItem(existingItems, hit)}
                    addLabel={addRecordLabel}
                    openLabel={openRecordLabel}
                  />
                ))}

                {totalPages > 1 && (
                  <div className="mt-15 text-align-center">
                    <Pagination
                      activePage={page}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      size="small"
                      siblingRange={1}
                      boundaryRange={0}
                      ellipsisItem={null}
                    />
                    <div
                      className="mt-5 font-size-small"
                    >
                      {i18next.t("{{total}} records found", { total })}
                    </div>
                  </div>
                )}
              </Dimmer.Dimmable>
            </Grid.Column>
          </Grid>
        </Modal.Content>

        <Modal.Actions>
          <Button onClick={handleClose}>{i18next.t("Close")}</Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

ExternalRecordSearchModal.propTypes = {
  apiPrefix: PropTypes.string.isRequired,
  buttonLabel: PropTypes.string.isRequired,
  modalTitle: PropTypes.string.isRequired,
  openRecordLabel: PropTypes.string,
  addRecordLabel: PropTypes.string,
  duplicateMessage: PropTypes.string,
  toItemObject: PropTypes.func.isRequired,
  isDuplicateItem: PropTypes.func.isRequired,
  existingItems: PropTypes.array,
  onAddItem: PropTypes.func.isRequired,
};

ExternalRecordSearchModal.defaultProps = {
  openRecordLabel: i18next.t("Open"),
  addRecordLabel: i18next.t("Add"),
  duplicateMessage: i18next.t("This record is already included."),
  existingItems: [],
};

export default ExternalRecordSearchModal;
